// Words and badges for loans, shared by the Loans tab and the playground.
import type { CurrencyLike } from "../../lib/money";
import { formatMoney } from "../../lib/money";
import type { CreditScore, Loan, LoanProduct, RepaymentFrequency } from "../../lib/types";

export const RATING_LABEL: Record<CreditScore["rating"], string> = {
  poor: "Poor", fair: "Fair", good: "Good", very_good: "Very good", excellent: "Excellent",
};

/** "weekly instalments" */
export function describeFrequency(f: RepaymentFrequency, plural = true): string {
  const word = { daily: "daily", weekly: "weekly", monthly: "monthly" }[f];
  return plural ? `${word} instalments` : `${word} instalment`;
}

const pct = (bps: number) => `${bps / 100}%`;

/** "28 days at 26% · 84 days at 30%" */
export function describeTerms(p: Pick<LoanProduct, "terms">): string {
  return p.terms.map((t) => `${t.days} days at ${pct(t.rate_bps)}`).join(" · ");
}

/** The product's score bands in words. */
export function describeBands(p: Pick<LoanProduct, "score_bands">, cur: CurrencyLike): string[] {
  return p.score_bands.map((b, i) => {
    const next = p.score_bands[i + 1];
    const range = next ? `${b.min_score}–${next.min_score - 1}` : `${b.min_score}+`;
    if (b.max_principal_minor === 0) return `Score ${range}: refused`;
    const adj = b.rate_adjust_bps ? `, rate ${b.rate_adjust_bps > 0 ? "+" : ""}${pct(b.rate_adjust_bps)}` : "";
    return `Score ${range}: up to ${formatMoney(b.max_principal_minor, cur)}${adj}`;
  });
}

export const LOAN_STATUS: Record<Loan["status"], string> = {
  active: "Active", paid_off: "Paid off", defaulted: "Defaulted", written_off: "Written off",
};

export function loanStatusBadge(l: Pick<Loan, "status" | "arrears_minor">) {
  const tone = l.status === "paid_off" ? "ok" : l.status === "active" ? (l.arrears_minor > 0 ? "bad" : "") : "bad";
  const text = l.status === "active" && l.arrears_minor > 0 ? "In arrears" : LOAN_STATUS[l.status];
  return <span className={`badge ${tone}`}>{text}</span>;
}
